import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAppId } from '../../context/AppIdContext';
import Header from '../../component/header';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const AudioPromotion = () => {
    // --- State ---
    const [audios, setAudios] = useState([]);
    const [loading, setLoading] = useState(false);
    
    // --- Track whether we are adding a new audio or changing an existing one
    const [uploadMode, setUploadMode] = useState('ADD');

    // --- Hooks & Context ---
    // Added userType to determine role
    const { appId, language, userId, userType } = useAppId();
    const lang = language || 'en';

    // --- Refs ---
    const fileInputRef = useRef(null);

    // --- Role Verification Logic ---
    const activeRole = userType || localStorage.getItem('userType') || localStorage.getItem('designation') || localStorage.getItem('role');
    const roleCode = activeRole ? activeRole.toUpperCase() : 'A';
    const isSubAdmin = roleCode === 'SA' || roleCode === 'SUB_ADMIN';

    // --- Translation Dictionary ---
    const t = {
        en: {
            pageTitle: "Audio Promotion",
            subtitle: "Listen to and share promotional audio",
            uploadedBy: "Uploaded :",
            loadingData: "Loading Audio...",
            noData: "No promotional audio found.",
            btnChange: "Change Audio",
            btnDelete: "Delete Audio",
            btnAdd: "Add Audio"
        },
        hi: {
            pageTitle: "ऑडियो प्रचार",
            subtitle: "प्रचार ऑडियो सुनें और साझा करें",
            uploadedBy: "अपलोड :",
            loadingData: "ऑडियो लोड हो रहा है...",
            noData: "कोई प्रचार ऑडियो नहीं मिला।",
            btnChange: "ऑडियो बदलें",
            btnDelete: "ऑडियो हटाएं",
            btnAdd: "ऑडियो जोड़ें"
        },
        gu: {
            pageTitle: "ઑડિઓ પ્રમોશન",
            subtitle: "પ્રમોશનલ ઑડિઓ સાંભળો અને શેર કરો",
            uploadedBy: "અપલોડ :",
            loadingData: "ઑડિઓ લોડ થઈ રહ્યો છે...",
            noData: "કોઈ પ્રમોશનલ ઑડિઓ મળ્યો નથી.",
            btnChange: "ઑડિઓ બદલો",
            btnDelete: "ઑડિઓ કાઢી નાખો",
            btnAdd: "ઑડિઓ ઉમેરો"
        }
    };

    const txt = t[lang];

    // --- API Fetch Logic ---
    const fetchAudios = async () => {
        if (!appId) return;

        setLoading(true);
        try {
            const response = await axios.post(
                `${BASE_URL}/api/dis_prachar_master`,
                { 
                    app_id: appId,
                    prachar_type: "audio" // Audio promotion type
                }
            );

            if (response.data.success && response.data.data && Array.isArray(response.data.data.result)) {
                setAudios(response.data.data.result);
            } else {
                setAudios([]);
            }
        } catch (error) {
            console.error("Error fetching audio promotions:", error);
            setAudios([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAudios();
    }, [appId]);

    // --- Global Add Action (INSERT API) ---
    const handleAddAudio = () => {
        setUploadMode('ADD');
        if (fileInputRef.current) {
            fileInputRef.current.click(); // Trigger the hidden file input
        }
    };

    const handleChangeAudio = () => {
        setUploadMode('CHANGE');
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    // --- Handle File Upload for both Add and Change actions
    const handleFileChange = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Convert the audio file to a Base64 string
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onloadend = async () => {
            // Split the string to remove the "data:audio/mp3;base64," prefix
            const base64String = reader.result.split(',')[1];

            try {
                setLoading(true);
                const payload = {
                    app_id: appId?.toString(),
                    prachar_type: "audio", // Correct type for Audio
                    base64: base64String, // Passing pure base64
                    content: file.name,   // Passing the filename
                    create_by: userId?.toString() || "1"
                };

                // If changing an existing audio, append its ID so the backend updates it
                if (uploadMode === 'CHANGE' && audios.length > 0) {
                    payload.id = audios[0].id.toString();
                }

                console.log(`Sending ${uploadMode} Payload:`, { ...payload, base64: "HIDDEN_BASE64" });

                const response = await axios.post(
                    `${BASE_URL}/api/ins_prachar_master`,
                    payload
                );

                if (response.data.success) {
                    await fetchAudios(); // Instantly reload to show the new audio
                } else {
                    alert("Failed to upload the audio. Server responded but success was false.");
                }
            } catch (error) {
                console.error("Error uploading audio:", error.response ? error.response.data : error.message);
                alert(`An error occurred while uploading the audio: ${error.message}`);
            } finally {
                setLoading(false);
                // Clear the input so the same file can be selected again if needed
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
            }
        };
    };

    // --- Individual Card Delete Action ---
    const handleDeleteAudio = async () => {
        if (audios.length === 0) return;

        if (window.confirm("Are you sure you want to delete this audio file?")) {
            try {
                // Taking the ID of the audio that was fetched
                const audioId = audios[0].id; 

                const response = await axios.post(
                    `${BASE_URL}/api/del_prachar_master`,
                    {
                        app_id: appId?.toString(),
                        id: audioId?.toString(),
                        delete_by: userId?.toString() || "1" 
                    }
                );

                if (response.data.success) {
                    // Re-fetch the audios to update the UI
                    fetchAudios();
                } else {
                    alert("Failed to delete the audio.");
                }
            } catch (error) {
                console.error("Error deleting audio:", error);
                alert("An error occurred while deleting the audio.");
            }
        }
    };

    // --- Loading State ---
    if (loading && audios.length === 0) {
        return (
            <div className="app-container p-4">
                <Header title={txt.pageTitle}/>
                <div className="loader-wrapper">
                    <div className="spinner-modern"></div>
                    <p className="loader-text">{txt.loadingData}</p>
                </div>
                <style jsx>{`
                    .app-container { background: #E5E7EB; min-height: 100vh; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; }
                    .loader-wrapper { display: flex; flex-direction: column; justify-content: center; align-items: center; min-height: 60vh; width: 100%; }
                    .spinner-modern { width: 50px; height: 50px; border: 4px solid #cbd5e1; border-top: 4px solid #6366f1; border-radius: 50%; animation: spin 1s linear infinite; margin-bottom: 16px; }
                    .loader-text { color: #64748b; font-weight: 600; font-size: 1.1rem; }
                    @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
                `}</style>
            </div>
        );
    }

    return (
        <div className='app-container p-4'>

            {/* Header */}
            <Header title={txt.pageTitle} />

            {/* Page Action Bar (Subtitle + Add Button) */}
            <div className="page-action-bar mt-3 mb-4">
                <div className="subtitle-container m-0">
                    <p className="subtitle-text">{txt.subtitle}</p>
                </div>
                
                <div className="global-buttons">
                    {/* Hidden file input for uploading audio files */}
                    <input 
                        type="file" 
                        accept="audio/*" 
                        ref={fileInputRef} 
                        style={{ display: 'none' }} 
                        onChange={handleFileChange} 
                    />

                    {/* CONDITIONAL RENDERING FOR BUTTONS - Hidden for Sub Admins */}
                    {!isSubAdmin && (
                        audios.length === 0 ? (
                            <button 
                                className="global-btn btn-add" 
                                onClick={handleAddAudio}
                            >
                                <svg fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                                    <line x1="12" y1="5" x2="12" y2="19"></line>
                                    <line x1="5" y1="12" x2="19" y2="12"></line>
                                </svg>
                                {txt.btnAdd}
                            </button>
                        ) : (
                            <>
                                <button 
                                    className="global-btn btn-edit" 
                                    onClick={handleChangeAudio}
                                >
                                    <svg fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                                        <path d="M12 20h9"></path>
                                        <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
                                    </svg>
                                    {txt.btnChange}
                                </button>

                                <button 
                                    className="global-btn btn-delete" 
                                    onClick={handleDeleteAudio}
                                >
                                    <svg fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                                        <polyline points="3 6 5 6 21 6"></polyline>
                                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                        <line x1="10" y1="11" x2="10" y2="17"></line>
                                        <line x1="14" y1="11" x2="14" y2="17"></line>
                                    </svg>
                                    {txt.btnDelete}
                                </button>
                            </>
                        )
                    )}
                </div>
            </div>

            {/* No Data State */}
            {audios.length === 0 && !loading && (
                <div className="row">
                    <div className="col-12 text-center text-muted p-5 empty-state">
                        <div className="empty-icon">🎧</div>
                        <p>{txt.noData}</p>
                    </div>
                </div>
            )}

            {/* --- AUDIO GRID --- */}
            <div className="audio-grid pb-5">
                {audios.map((audio) => (
                    <div className="audio-card" key={audio.id}>
                        {/* Audio Player Container */}
                        <div className="media-wrapper">
                            <div className="audio-icon-bg">🎵</div>
                            <audio 
                                src={audio.content} 
                                className="promo-audio"
                                controls
                                preload="metadata"
                            >
                                Your browser does not support the audio element.
                            </audio>
                        </div>

                        {/* Uploader Details Container */}
                        <div className="info-wrapper">
                            <div className="upload-info">
                                <span className="upload-label">{txt.uploadedBy}</span>
                                <span className="upload-name">{audio.name || "Unknown"}</span>
                            </div>
                            <div className="designation-badge">
                                {audio.designation || "User"}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <style jsx>{`
                .app-container {
                    background: #E5E7EB;
                    min-height: 100vh;
                    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
                }

                /* --- PAGE ACTION BAR --- */
                .page-action-bar {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    flex-wrap: wrap;
                    gap: 16px;
                }

                .subtitle-text {
                    font-size: 15px;
                    color: #475569;
                    font-weight: 600;
                    margin: 0;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .global-buttons {
                    display: flex;
                    gap: 12px;
                }

                .global-btn {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 10px 16px;
                    border-radius: 10px;
                    font-size: 13px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    border: none;
                    box-shadow: 0 1px 2px rgba(0,0,0,0.05);
                }

                .global-btn svg {
                    width: 16px;
                    height: 16px;
                }

                /* Add Button Global (Solid Indigo Theme for Audio) */
                .btn-add {
                    background: #6366f1; /* Indigo color */
                    color: #ffffff;
                    border: 1px solid #4f46e5;
                    box-shadow: 0 4px 6px -1px rgba(99, 102, 241, 0.2);
                }
                .btn-add:hover {
                    background: #4f46e5;
                    border-color: #4338ca;
                    transform: translateY(-1px);
                    box-shadow: 0 6px 8px -1px rgba(99, 102, 241, 0.3);
                }
                .btn-add:active {
                    background: #4338ca;
                    transform: translateY(0);
                }

                /* Edit Button Global */
                .btn-edit {
                    background: #ffffff;
                    color: #334155;
                    border: 1px solid #cbd5e1;
                }
                .btn-edit:hover {
                    background: #f8fafc;
                    color: #0f172a;
                    border-color: #94a3b8;
                }
                .btn-edit:active {
                    background: #f1f5f9;
                }

                /* Delete Button Global */
                .btn-delete {
                    background: #fef2f2;
                    color: #ef4444;
                    border: 1px solid #fecaca;
                }
                .btn-delete:hover {
                    background: #fee2e2;
                    color: #dc2626;
                    border-color: #fca5a5;
                }
                .btn-delete:active {
                    background: #fecaca;
                }

                /* --- AUDIO GRID --- */
                .audio-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
                    gap: 24px;
                }

                /* --- PREMIUM CARD STYLES --- */
                .audio-card {
                    background: #ffffff;
                    border-radius: 16px;
                    overflow: hidden;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
                    border: 1px solid #f1f5f9;
                    display: flex;
                    flex-direction: column;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                }

                .audio-card:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 15px 30px -5px rgba(0, 0, 0, 0.1);
                    border-color: #e2e8f0;
                }

                /* Media Styling */
                .media-wrapper {
                    width: 100%;
                    height: 140px; /* Shorter height for audio */
                    background: #eef2ff; /* Light indigo background */
                    position: relative;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    gap: 15px;
                    border-bottom: 1px solid #e0e7ff;
                }

                .audio-icon-bg {
                    font-size: 2.5rem;
                    opacity: 0.8;
                }

                .promo-audio {
                    width: 90%;
                    height: 40px;
                    outline: none;
                }

                /* Bottom Info Area */
                .info-wrapper {
                    padding: 16px 20px;
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                    background: #ffffff;
                }

                .upload-info {
                    display: flex;
                    align-items: baseline;
                    gap: 6px;
                    flex-wrap: wrap;
                }

                .upload-label {
                    font-size: 13px;
                    color: #64748b;
                    font-weight: 500;
                }

                .upload-name {
                    font-size: 15px;
                    color: #0f172a;
                    font-weight: 700;
                }

                /* Designation Badge */
                .designation-badge {
                    align-self: flex-start;
                    font-size: 11px;
                    font-weight: 700;
                    color: #6366f1; /* Match Audio Indigo Theme */
                    background: #eef2ff;
                    padding: 4px 10px;
                    border-radius: 20px;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    border: 1px solid #c7d2fe;
                }

                /* Empty State */
                .empty-state { 
                    background: white; 
                    border-radius: 20px; 
                    border: 2px dashed #cbd5e1; 
                }
                .empty-icon { 
                    font-size: 3rem; 
                    margin-bottom: 10px; 
                    opacity: 0.5; 
                }

                /* Mobile Optimization */
                @media (max-width: 640px) {
                    .page-action-bar {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 12px;
                    }
                    .global-buttons {
                        width: 100%;
                        display: flex;
                    }
                    .global-btn {
                        flex: 1;
                        justify-content: center;
                    }
                    .audio-grid { 
                        grid-template-columns: 1fr; 
                        gap: 16px;
                    }
                }
            `}</style>
        </div>
    );
};

export default AudioPromotion;